import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Security as SecurityIcon } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { fetchPermissions, fetchRolePermissions, saveRolePermissions, fetchRoleById, updateRole } from '../api/features/roles/rolesApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';

export default function RoleEdit() {
  const navigate = useNavigate();
  const { roleId } = useParams<{ roleId: string }>();
  const location = useLocation();
  const { roleName: initialRoleName } = location.state || {};
  const { clientCode } = useClientCode();

  // State
  const [roleData, setRoleData] = useState<any>(null);
  const [roleName, setRoleName] = useState('');
  const [permissions, setPermissions] = useState<any[]>([]);
  const [assignedPermissions, setAssignedPermissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const sectionSx = {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: '8px',
    border: `1px solid ${SAP_COLORS.sapGray2}`,
    boxShadow: SAP_SHADOWS.sm,
    height: '500px',
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
  };

  const fetchData = async () => {
    if (!roleId) return;

    setLoading(true);
    try {
      const [roleDataRes, permissionsData, rolePermissionsData] = await Promise.all([
        fetchRoleById({ clientCode, roleId: Number(roleId) }).catch(() => null),
        fetchPermissions({ clientCode }),
        fetchRolePermissions({ clientCode, roleId: Number(roleId) }),
      ]);

      if (roleDataRes) {
        setRoleData(roleDataRes);
        setRoleName(roleDataRes.roleName ?? initialRoleName ?? '');
      } else {
        setRoleName(initialRoleName ?? '');
      }

      setPermissions(permissionsData);
      const normalizedAssigned = rolePermissionsData
        .map((rp: any) => {
          const match = permissionsData.find(
            (p: any) => p.id === rp.id || p.permissionName === rp.permissionName
          );
          return match || { id: rp.id, permissionName: rp.permissionName };
        })
        .filter((rp: any) => rp.id != null && rp.permissionName);
      setAssignedPermissions(normalizedAssigned);
    } catch (err) {
      toast.error('Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [roleId]);

  const isAssigned = (permissionId: number) => assignedPermissions.some(ap => ap.id === permissionId);

  const handleAssignSingle = (permission: any) => {
    if (isAssigned(permission.id)) return;
    setAssignedPermissions(prev => [...prev, permission]);
  };

  const handleUnassignSingle = (permission: any) => {
    setAssignedPermissions(prev => prev.filter(p => p.id !== permission.id));
  };

  const handleBack = () => {
    navigate('/roles');
  };

  const handleUpdate = async () => {
    if (!roleId) return;
    setSaving(true);
    try {
      if (roleData && roleName.trim() && roleName !== roleData.roleName) {
        await updateRole({
          clientCode,
          payload: {
            ...roleData,
            id: roleData.id ?? Number(roleId),
            roleName: roleName.trim(),
            lastUpdateDate: Date.now(),
            lastUpdatedBy: roleData.lastUpdatedBy ?? roleData.createdBy,
          },
        });
      }
      await saveRolePermissions({
        clientCode,
        roleId: Number(roleId),
        permissionIds: assignedPermissions.map((p) => p.id),
      });
      toast.success('Role updated successfully!');
      fetchData();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: SAP_SPACING.lg, bgcolor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
      {/* Header with back arrow */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: SAP_SPACING.xl }}>
        <IconButton
          onClick={handleBack}
          sx={{
            marginRight: SAP_SPACING.md,
            backgroundColor: SAP_COLORS.sapGray2,
            color: SAP_COLORS.sapGray7,
            '&:hover': { backgroundColor: SAP_COLORS.sapGray3 },
          }}
          aria-label="Back to Roles"
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: SAP_SPACING.sm }}>
          <SecurityIcon sx={{ color: SAP_COLORS.sapBlue, fontSize: '2rem' }} />
          <Box>
            <Typography variant="h4" sx={{ color: SAP_COLORS.sapGray7, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold }}>
              {roleName || 'Role Edit'}
            </Typography>
            <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5, fontSize: '0.9375rem' }}>
              Manage role permissions
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Role Name Section */}
      <Paper sx={{ mb: 3, bgcolor: SAP_COLORS.sapWhite, p: 3, borderRadius: 2, border: `1px solid ${SAP_COLORS.sapGray2}` }}>
        <Typography variant="h6" sx={{ color: SAP_COLORS.sapGray7, mb: 2, fontWeight: SAP_TYPOGRAPHY.fontWeight.bold }}>
          Role
        </Typography>
        <TextField
          label="Role Name"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          fullWidth
          size="small"
          placeholder="Enter role name"
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: SAP_COLORS.sapWhite,
              '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
              '&:hover fieldset': { borderColor: SAP_COLORS.sapBlue },
              '&.Mui-focused fieldset': { borderColor: SAP_COLORS.sapBlue },
            },
            '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
            '& .MuiInputBase-input': { color: SAP_COLORS.sapGray7 },
          }}
        />
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} />
        </Box>
      )}

      {/* Permissions Management */}
      {!loading && (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch' }}>
          {/* Permissions */}
          <Paper sx={sectionSx}>
            <Box sx={{ 
              bgcolor: SAP_COLORS.sapBlue, 
              color: SAP_COLORS.sapWhite, 
              p: 2, 
              borderTopLeftRadius: 8, 
              borderTopRightRadius: 8,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              fontSize: '0.9375rem',
            }}>
              Permissions
            </Box>
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {permissions.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No permissions available" sx={{ color: SAP_COLORS.sapGray5, textAlign: 'center' }} />
                </ListItem>
              ) : (
                permissions.map((permission) => {
                  const assigned = isAssigned(permission.id);
                  return (
                    <ListItem key={permission.id} disablePadding>
                      {assigned ? (
                        <ListItemButton
                          disabled
                          sx={{
                            opacity: 0.6,
                            cursor: 'default',
                            '&.Mui-disabled': { opacity: 0.6 },
                          }}
                        >
                          <ListItemText primary={permission.permissionName} sx={{ color: SAP_COLORS.sapGray5 }} />
                        </ListItemButton>
                      ) : (
                        <ListItemButton
                          onClick={() => handleAssignSingle(permission)}
                          sx={{
                            '&:hover': { bgcolor: SAP_COLORS.sapGray1 },
                          }}
                        >
                          <ListItemText primary={permission.permissionName} sx={{ color: SAP_COLORS.sapGray7 }} />
                        </ListItemButton>
                      )}
                    </ListItem>
                  );
                })
              )}
            </List>
          </Paper>

          {/* Assigned Permissions */}
          <Paper sx={sectionSx}>
            <Box sx={{ 
              bgcolor: SAP_COLORS.sapBlue, 
              color: SAP_COLORS.sapWhite, 
              p: 2, 
              borderTopLeftRadius: 8, 
              borderTopRightRadius: 8,
              fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
              fontSize: '0.9375rem',
            }}>
              Assigned Permissions
            </Box>
            <List sx={{ flex: 1, overflow: 'auto' }}>
              {assignedPermissions.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No assigned permissions" sx={{ color: SAP_COLORS.sapGray5, textAlign: 'center' }} />
                </ListItem>
              ) : (
                assignedPermissions.map((permission) => (
                  <ListItem key={permission.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleUnassignSingle(permission)}
                      sx={{
                        '&:hover': { bgcolor: SAP_COLORS.sapGray1 },
                      }}
                    >
                      <ListItemText primary={permission.permissionName} sx={{ color: SAP_COLORS.sapGray7 }} />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Box>
      )}

      {!loading && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button onClick={handleBack} disabled={saving} sx={{ color: SAP_COLORS.sapGray6 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={saving}
            sx={{ bgcolor: SAP_COLORS.sapBlue, '&:hover': { bgcolor: SAP_COLORS.sapBlueDark } }}
          >
            {saving ? 'Saving...' : 'Update'}
          </Button>
        </Box>
      )}
    </Box>
  );
} 